import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { CalendarModule } from 'primeng/calendar';
import { MessageService, ConfirmationService } from 'primeng/api';
import { NotificationService, NotificationData } from '../../../services/notification.service';

@Component({
  selector: 'app-manage-notifications',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    TableModule,
    DialogModule,
    InputTextModule,
    DropdownModule,
    CheckboxModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    CalendarModule
  ],
  templateUrl: './manage-notifications.component.html',
  styleUrls: ['./manage-notifications.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class ManageNotificationsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private notificationService = inject(NotificationService);

  notifications = signal<NotificationData[]>([]);
  isLoading = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);

  showCreateDialog = false;
  showPreviewDialog = false;
  previewNotification: NotificationData | null = null;

  notificationForm: FormGroup;

  notificationTypes = [
    { label: 'Information', value: 'info', icon: 'pi-info-circle', color: 'blue' },
    { label: 'Avertissement', value: 'warning', icon: 'pi-exclamation-triangle', color: 'orange' },
    { label: 'Succès', value: 'success', icon: 'pi-check-circle', color: 'green' },
    { label: 'Erreur', value: 'error', icon: 'pi-times-circle', color: 'red' }
  ];

  iconOptions = [
    { label: 'Cloche', value: 'pi-bell' },
    { label: 'Information', value: 'pi-info-circle' },
    { label: 'Avertissement', value: 'pi-exclamation-triangle' },
    { label: 'Succès', value: 'pi-check-circle' },
    { label: 'Erreur', value: 'pi-times-circle' },
    { label: 'Étoile', value: 'pi-star' },
    { label: 'Feu', value: 'pi-fire' },
    { label: 'Coeur', value: 'pi-heart' },
    { label: 'Éclair', value: 'pi-bolt' },
    { label: 'Cadeau', value: 'pi-gift' }
  ];

  colorOptions = [
    { label: 'Bleu', value: 'blue' },
    { label: 'Vert', value: 'green' },
    { label: 'Orange', value: 'orange' },
    { label: 'Rouge', value: 'red' },
    { label: 'Violet', value: 'purple' },
    { label: 'Rose', value: 'pink' },
    { label: 'Gris', value: 'gray' }
  ];

  stats = signal({
    total: 0,
    active: 0,
    inactive: 0,
    totalReads: 0
  });

  constructor() {
    this.notificationForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      message: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]],
      notification_type: ['info', Validators.required],
      icon: ['pi-bell', Validators.required],
      icon_color: ['blue', Validators.required],
      expires_at: [null]
    });
  }

  ngOnInit() {
    this.loadNotifications();
  }

  loadNotifications() {
    this.isLoading.set(true);
    
    this.notificationService.getAllNotificationsAdmin().subscribe({
      next: (notifications) => {
        this.notifications.set(notifications);
        this.calculateStats(notifications);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Erreur lors du chargement des notifications:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les notifications',
          life: 5000
        });
        this.isLoading.set(false);
      }
    });
  }

  calculateStats(notifications: NotificationData[]) {
    const stats = {
      total: notifications.length,
      active: notifications.filter(n => n.is_active).length,
      inactive: notifications.filter(n => !n.is_active).length,
      totalReads: notifications.reduce((sum, n) => sum + (n.users_read_count || 0), 0)
    };
    this.stats.set(stats);
  }

  openCreateDialog() {
    this.notificationForm.reset();
    this.notificationForm.patchValue({
      notification_type: 'info',
      icon: 'pi-bell',
      icon_color: 'blue'
    });
    this.showCreateDialog = true;
  }

  onSubmitNotification() {
    if (this.notificationForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isSubmitting.set(true);
    const formData = this.notificationForm.value;
    
    const adminUser = JSON.parse(localStorage.getItem('user') || '{}');
    const notificationData: NotificationData = {
      ...formData,
      created_by: adminUser.user_id,
      expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
    };

    this.notificationService.createNotification(notificationData).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Notification créée',
          detail: 'La notification a été envoyée à tous les utilisateurs',
          life: 5000
        });
        
        this.showCreateDialog = false;
        this.notificationForm.reset();
        this.loadNotifications();
        this.isSubmitting.set(false);
      },
      error: (error) => {
        console.error('Erreur lors de la création:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error?.error || 'Impossible de créer la notification',
          life: 5000
        });
        this.isSubmitting.set(false);
      }
    });
  }

  previewNotificationDialog(notification: NotificationData) {
    this.previewNotification = notification;
    this.showPreviewDialog = true;
  }

  deactivateNotification(notification: NotificationData) {
    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir désactiver la notification "${notification.title}" ?`,
      header: 'Confirmer la désactivation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Désactiver',
      rejectLabel: 'Annuler',
      accept: () => {
        this.notificationService.deactivateNotification(notification.id!).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Notification désactivée',
              detail: 'La notification ne sera plus visible pour les utilisateurs',
              life: 3000
            });
            this.loadNotifications();
          },
          error: (error) => {
            console.error('Erreur lors de la désactivation:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Impossible de désactiver la notification',
              life: 5000
            });
          }
        });
      }
    });
  }

  getNotificationTypeSeverity(type: string): "success" | "secondary" | "info" | "warn" | "danger" | "contrast" | undefined {
    switch (type) {
      case 'info': return 'info';
      case 'warning': return 'warn';
      case 'success': return 'success';
      case 'error': return 'danger';
      default: return 'secondary';
    }
  }

  getNotificationTypeLabel(type: string): string {
    const typeObj = this.notificationTypes.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  }

  getReadPercentage(notification: NotificationData): number {
    const total = notification.total_users_notified || 0;
    const read = notification.users_read_count || 0;
    return total > 0 ? Math.round((read / total) * 100) : 0;
  }

  onNotificationTypeChange() {
    const selectedType = this.notificationForm.get('notification_type')?.value;
    const typeConfig = this.notificationTypes.find(t => t.value === selectedType);
    
    if (typeConfig) {
      this.notificationForm.patchValue({
        icon: typeConfig.icon,
        icon_color: typeConfig.color
      });
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.notificationForm.controls).forEach(key => {
      this.notificationForm.get(key)?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.notificationForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.notificationForm.get(fieldName);
    if (field && field.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) return `${fieldName} est requis`;
      if (field.errors['minlength']) return `${fieldName} trop court`;
      if (field.errors['maxlength']) return `${fieldName} trop long`;
    }
    return '';
  }

  refreshNotifications() {
    this.loadNotifications();
    this.messageService.add({
      severity: 'info',
      summary: 'Actualisé',
      detail: 'La liste des notifications a été mise à jour',
      life: 3000
    });
  }
}